import React, { useState, useEffect } from 'react';
import { Upload, Lock, Unlock, Save, Building, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface PropertyLogo {
  propertyId: string;
  logo: string;
  settings: {
    locked: boolean;
    invoiceSize: number;
    leaseSize: number;
  };
}

interface PropertyRegistration {
  name: string;
  description: string;
  address: {
    street: string;
    suburb: string;
    city: string;
    postalCode: string;
  };
  features: string[];
  amenities: string[];
  contactInfo: {
    email: string;
    phone: string;
    website?: string;
  };
  ownerInfo: {
    name: string;
    email: string;
    phone: string;
    companyName: string;
    registrationNumber: string;
  };
}

const PropertiesView = () => {
  const { user, isSuperAdmin } = useAuth();
  const [selectedProperty, setSelectedProperty] = useState('saratoga');
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState<PropertyRegistration[]>([]);
  const [registrationForm, setRegistrationForm] = useState<PropertyRegistration>({
    name: '',
    description: '',
    address: {
      street: '',
      suburb: '',
      city: '',
      postalCode: '',
    },
    features: [],
    amenities: [],
    contactInfo: {
      email: '',
      phone: '',
      website: '',
    },
    ownerInfo: {
      name: '',
      email: '',
      phone: '',
      companyName: '',
      registrationNumber: '',
    },
  });
  const [propertyLogos, setPropertyLogos] = useState<Record<string, PropertyLogo>>(() => {
    const stored = localStorage.getItem('propertyLogos');
    return stored ? JSON.parse(stored) : {
      saratoga: {
        propertyId: 'saratoga',
        logo: null,
        settings: {
          locked: false,
          invoiceSize: 50,
          leaseSize: 50,
        }
      },
      highlands: {
        propertyId: 'highlands',
        logo: null,
        settings: {
          locked: false,
          invoiceSize: 50,
          leaseSize: 50,
        }
      }
    };
  });

  useEffect(() => {
    localStorage.setItem('propertyLogos', JSON.stringify(propertyLogos));
  }, [propertyLogos]);

  const currentProperty = propertyLogos[selectedProperty];

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currentProperty.settings.locked) {
      alert('Logo is locked. Unlock it first to make changes.');
      return;
    }

    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const maxWidth = 800;
          const maxHeight = 400;

          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const base64String = canvas.toDataURL('image/jpeg', 0.8);
          
          setPropertyLogos(prev => ({
            ...prev,
            [selectedProperty]: {
              ...prev[selectedProperty],
              logo: base64String
            }
          }));
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleLock = () => {
    setPropertyLogos(prev => ({
      ...prev,
      [selectedProperty]: {
        ...prev[selectedProperty],
        settings: {
          ...prev[selectedProperty].settings,
          locked: !prev[selectedProperty].settings.locked
        }
      }
    }));
  };

  const handleSizeChange = (type: 'invoice' | 'lease', value: number) => {
    if (currentProperty.settings.locked) {
      alert('Logo is locked. Unlock it first to make changes.');
      return;
    }

    setPropertyLogos(prev => ({
      ...prev,
      [selectedProperty]: {
        ...prev[selectedProperty],
        settings: {
          ...prev[selectedProperty].settings,
          [type === 'invoice' ? 'invoiceSize' : 'leaseSize']: value
        }
      }
    }));
  };

  const saveSettings = () => {
    localStorage.setItem('propertyLogos', JSON.stringify(propertyLogos));
    alert('Logo settings saved successfully!');
  };

  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('property_registrations')
        .insert({
          ...registrationForm,
          status: 'pending',
          submitted_by: user?.id,
          submitted_at: new Date().toISOString(),
        });

      if (error) throw error;

      alert('Property registration submitted successfully! Awaiting admin approval.');
      setShowRegistrationForm(false);
      setRegistrationForm({
        name: '',
        description: '',
        address: {
          street: '',
          suburb: '',
          city: '',
          postalCode: '',
        },
        features: [],
        amenities: [],
        contactInfo: {
          email: '',
          phone: '',
          website: '',
        },
        ownerInfo: {
          name: '',
          email: '',
          phone: '',
          companyName: '',
          registrationNumber: '',
        },
      });
    } catch (error) {
      console.error('Error submitting registration:', error);
      alert('Failed to submit registration. Please try again.');
    }
  };

  const handleApproveRegistration = async (registration: PropertyRegistration) => {
    try {
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .insert({
          name: registration.name,
          description: registration.description,
          address: registration.address,
          features: registration.features,
          amenities: registration.amenities,
          contact_info: registration.contactInfo,
          owner_info: registration.ownerInfo,
          status: 'active',
        })
        .select()
        .single();

      if (propertyError) throw propertyError;

      const { error: updateError } = await supabase
        .from('property_registrations')
        .update({ status: 'approved', approved_at: new Date().toISOString() })
        .eq('id', registration.id);

      if (updateError) throw updateError;

      setPendingRegistrations(prev => 
        prev.filter(reg => reg.id !== registration.id)
      );

      alert('Property registration approved successfully!');
    } catch (error) {
      console.error('Error approving registration:', error);
      alert('Failed to approve registration. Please try again.');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Properties Management</h1>
        <button
          onClick={() => setShowRegistrationForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Register New Property
        </button>
      </div>

      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Register New Property</h2>
            
            <form onSubmit={handleRegistrationSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Property Name</label>
                    <input
                      type="text"
                      value={registrationForm.name}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={registrationForm.description}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Street Address</label>
                    <input
                      type="text"
                      value={registrationForm.address.street}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          street: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Suburb</label>
                    <input
                      type="text"
                      value={registrationForm.address.suburb}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          suburb: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={registrationForm.address.city}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          city: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      value={registrationForm.address.postalCode}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          postalCode: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Features & Amenities</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Features</label>
                    <input
                      type="text"
                      placeholder="Add features separated by commas"
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        features: e.target.value.split(',').map(f => f.trim())
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amenities</label>
                    <input
                      type="text"
                      placeholder="Add amenities separated by commas"
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        amenities: e.target.value.split(',').map(a => a.trim())
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={registrationForm.contactInfo.email}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        contactInfo: {
                          ...prev.contactInfo,
                          email: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={registrationForm.contactInfo.phone}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        contactInfo: {
                          ...prev.contactInfo,
                          phone: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <input
                      type="url"
                      value={registrationForm.contactInfo.website}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        contactInfo: {
                          ...prev.contactInfo,
                          website: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Owner Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Owner Name</label>
                    <input
                      type="text"
                      value={registrationForm.ownerInfo.name}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        ownerInfo: {
                          ...prev.ownerInfo,
                          name: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Owner Email</label>
                    <input
                      type="email"
                      value={registrationForm.ownerInfo.email}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        ownerInfo: {
                          ...prev.ownerInfo,
                          email: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Owner Phone</label>
                    <input
                      type="tel"
                      value={registrationForm.ownerInfo.phone}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        ownerInfo: {
                          ...prev.ownerInfo,
                          phone: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={registrationForm.ownerInfo.companyName}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        ownerInfo: {
                          ...prev.ownerInfo,
                          companyName: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                    <input
                      type="text"
                      value={registrationForm.ownerInfo.registrationNumber}
                      onChange={(e) => setRegistrationForm(prev => ({
                        ...prev,
                        ownerInfo: {
                          ...prev.ownerInfo,
                          registrationNumber: e.target.value
                        }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowRegistrationForm(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Registration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isSuperAdmin() && pendingRegistrations.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Pending Registrations</h2>
          <div className="space-y-4">
            {pendingRegistrations.map((registration) => (
              <div key={registration.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium">{registration.name}</h3>
                    <p className="text-gray-600">{registration.description}</p>
                  </div>
                  <button
                    onClick={() => handleApproveRegistration(registration)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Approve
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium">Owner</p>
                    <p>{registration.ownerInfo.name}</p>
                    <p>{registration.ownerInfo.companyName}</p>
                  </div>
                  <div>
                    <p className="font-medium">Location</p>
                    <p>{registration.address.street}</p>
                    <p>{registration.address.suburb}, {registration.address.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold">Property Logo</h2>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="saratoga">21 Saratoga</option>
              <option value="highlands">Highlands Lofts</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleLock}
              className={`p-2 rounded-md ${
                currentProperty.settings.locked 
                  ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
              }`}
              title={currentProperty.settings.locked ? 'Unlock logo settings' : 'Lock logo settings'}
            >
              {currentProperty.settings.locked ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
            </button>
            <button
              onClick={saveSettings}
              className="p-2 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200"
              title="Save logo settings"
            >
              <Save className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex items-start space-x-6">
          {currentProperty.logo ? (
            <div className="w-48 h-24 border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
              <img 
                src={currentProperty.logo} 
                alt={`${selectedProperty} Logo`} 
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-48 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <Building className="w-8 h-8 text-gray-400" />
            </div>
          )}
          
          <div className="flex-1 space-y-4">
            <div>
              <label className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                <Upload className="h-4 w-4 mr-2" />
                Upload Logo
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={currentProperty.settings.locked}
                />
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Recommended size: 800x400 pixels or smaller.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Logo Size (px)
                </label>
                <input
                  type="range"
                  min="30"
                  max="150"
                  value={currentProperty.settings.invoiceSize}
                  onChange={(e) => handleSizeChange('invoice', parseInt(e.target.value))}
                  disabled={currentProperty.settings.locked}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>30px</span>
                  <span>{currentProperty.settings.invoiceSize}px</span>
                  <span>150px</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Agreement Logo Size (px)
                </label>
                <input
                  type="range"
                  min="30"
                  max="150"
                  value={currentProperty.settings.leaseSize}
                  onChange={(e) => handleSizeChange('lease', parseInt(e.target.value))}
                  disabled={currentProperty.settings.locked}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>30px</span>
                  <span>{currentProperty.settings.leaseSize}px</span>
                  <span>150px</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Properties</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 border rounded-lg">
            <h3 className="text-lg font-medium mb-2">21 Saratoga</h3>
            <p className="text-gray-600 mb-4">Premium student accommodation in a prime location</p>
            <img
              src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267"
              alt="21 Saratoga"
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
          </div>
          <div className="p-4 border rounded-lg">
            <h3 className="text-lg font-medium mb-2">Highlands Lofts</h3>
            <p className="text-gray-600 mb-4">Modern urban living in the heart of the city</p>
            <img
              src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00"
              alt="Highlands Lofts"
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesView;